import { StoreHistoryOverride } from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import {
  DATABASE_ROW_SHARD_CONDENSE_MAP_KEY,
  databaseDocumentCollectionRoot,
} from '@streamlain/database-identity';
import {
  type LocalPublicationReceipt,
  PublicationError,
  type PublicationKind,
} from '@streamlain/doc-publication-contract';
import type {
  DatabaseSemanticChange,
  DatabaseSemanticSnapshot,
} from '@streamlain/doc-publication-contract/database-writer';
import {
  equal,
  identity,
  identityKey,
  type SerializedValue,
} from '@streamlain/doc-publication-contract/database-writer';
import { Array as YArray, type Doc, Map as YMap, Text as YText } from 'yjs';
export {
  type DatabaseSemanticChange,
  type DatabaseSemanticSnapshot,
  diffDatabaseSemantics,
} from '@streamlain/doc-publication-contract/database-writer';

import type { DatabaseRowMutation } from './data-source-contracts.js';

function serialize(value: unknown): SerializedValue {
  if (value instanceof YMap)
    return {
      kind: 'map',
      entries: [...value].map(([key, child]) => [key, serialize(child)]),
    };
  if (value instanceof YArray)
    return { kind: 'array', items: value.toArray().map(serialize) };
  if (value instanceof YText)
    return { kind: 'text', delta: structuredClone(value.toDelta()) };
  return { kind: 'value', value: structuredClone(value) };
}
function restore(value: SerializedValue): unknown {
  if (value.kind === 'map')
    return new YMap(value.entries.map(([key, child]) => [key, restore(child)]));
  if (value.kind === 'array') {
    const array = new YArray();
    array.push(value.items.map(restore));
    return array;
  }
  if (value.kind === 'text') {
    const text = new YText();
    text.applyDelta(value.delta);
    return text;
  }
  return structuredClone(value.value);
}

export function snapshotDatabaseSemantics(doc: Doc): DatabaseSemanticSnapshot {
  return new Map(
    [...doc.share.keys()]
      // The condense marker asks for server maintenance; undo never owns it.
      .filter(name => name !== DATABASE_ROW_SHARD_CONDENSE_MAP_KEY)
      .map(name => [
        name,
        serialize(
          databaseDocumentCollectionRoot(name) === 'array'
            ? doc.getArray(name)
            : doc.getMap(name)
        ),
      ])
  );
}

function reconcileArray(
  target: YArray<unknown>,
  before: readonly SerializedValue[],
  after: readonly SerializedValue[]
): void {
  const old = new Map(before.map(value => [identityKey(value), value]));
  const wanted = new Map(after.map(value => [identityKey(value), value]));
  const keyed =
    old.size === before.length &&
    wanted.size === after.length &&
    before.every(identity) &&
    after.every(identity);
  if (!keyed) {
    const current = target.toArray().map(serialize);
    let prefix = 0,
      suffix = 0;
    while (
      prefix < Math.min(current.length, after.length) &&
      equal(current[prefix]!, after[prefix]!)
    )
      prefix++;
    while (
      suffix < Math.min(current.length, after.length) - prefix &&
      equal(
        current[current.length - 1 - suffix]!,
        after[after.length - 1 - suffix]!
      )
    )
      suffix++;
    target.delete(prefix, current.length - prefix - suffix);
    target.insert(
      prefix,
      after.slice(prefix, after.length - suffix).map(restore)
    );
    return;
  }
  for (let index = target.length - 1; index >= 0; index--) {
    const key = identityKey(serialize(target.get(index)));
    if (old.has(key) && !wanted.has(key)) target.delete(index, 1);
  }
  for (const [index, value] of after.entries()) {
    const key = identityKey(value);
    const current = target.toArray().map(item => identityKey(serialize(item)));
    const present = current.indexOf(key);
    // The reverse edit owns the changed address order; unrelated new entries survive.
    const previous = index
      ? current.indexOf(identityKey(after[index - 1]!))
      : -1;
    const position = previous + 1;
    if (present === -1) target.insert(position, [restore(value)]);
    else if (
      present !== position &&
      JSON.stringify(before.map(identityKey)) !==
        JSON.stringify(after.map(identityKey))
    ) {
      const moved = serialize(target.get(present));
      target.delete(present, 1);
      const remaining = target
        .toArray()
        .map(item => identityKey(serialize(item)));
      const anchor = index
        ? remaining.indexOf(identityKey(after[index - 1]!)) + 1
        : 0;
      target.insert(anchor, [restore(moved)]);
    }
  }
}

export function applyDatabaseSemanticChange(
  doc: Doc,
  change: DatabaseSemanticChange
): void {
  let parent: unknown = doc.share.get(change.collection);
  if (!parent) {
    parent =
      change.after.kind === 'array' || typeof change.path[0] === 'object'
        ? doc.getArray(change.collection)
        : doc.getMap(change.collection);
  }
  if (!change.path.length) {
    if (
      parent instanceof YArray &&
      change.before.kind === 'array' &&
      change.after.kind === 'array'
    ) {
      reconcileArray(parent, change.before.items, change.after.items);
      return;
    }
    if (parent instanceof YMap && change.after.kind === 'map') {
      const desired = new Map(change.after.entries);
      for (const key of parent.keys())
        if (!desired.has(key)) parent.delete(key);
      for (const [key, value] of desired) parent.set(key, restore(value));
      return;
    }
    throw new PublicationError('FORMAT_MISMATCH');
  }
  for (const part of change.path.slice(0, -1)) {
    if (typeof part === 'string' && parent instanceof YMap)
      parent = parent.get(part);
    else if (typeof part === 'object' && parent instanceof YArray)
      parent = parent
        .toArray()
        .find(
          value => value instanceof YMap && value.get(part.key) === part.value
        );
    else throw new PublicationError('LOCAL_CONFLICT');
  }
  const last = change.path.at(-1);
  // An entry of a list with identity: set at its place, appended when new,
  // removed when absent.
  if (typeof last === 'object' && parent instanceof YArray) {
    const index = parent
      .toArray()
      .findIndex(
        value => value instanceof YMap && value.get(last.key) === last.value
      );
    if (index !== -1) parent.delete(index, 1);
    if (!(change.after.kind === 'value' && change.after.value === undefined))
      parent.insert(index === -1 ? parent.length : index, [
        restore(change.after),
      ]);
    return;
  }
  if (!(parent instanceof YMap) || typeof last !== 'string')
    throw new PublicationError('LOCAL_CONFLICT');
  const current = parent.get(last);
  if (
    current instanceof YArray &&
    change.before.kind === 'array' &&
    change.after.kind === 'array'
  )
    reconcileArray(current, change.before.items, change.after.items);
  else if (change.after.kind === 'value' && change.after.value === undefined)
    parent.delete(last);
  else parent.set(last, restore(change.after));
}

function inverseKind(kind: PublicationKind): PublicationKind {
  return kind === 'create' ? 'delete' : kind === 'delete' ? 'create' : kind;
}
export interface DeferredDatabaseHistory {
  count: number;
  read(index: number): Promise<DatabaseSemanticChange[]>;
  release(): Promise<void>;
}
type Commit = (
  input: DatabaseRowMutation
) => Promise<LocalPublicationReceipt | null>;
/** Undo steps kept per database history; the oldest step falls out first. An
 * import is one step, however many rows it brings. */
export const DATABASE_UNDO_STEP_LIMIT = 100;
type Entry = {
  deferred?: DeferredDatabaseHistory;
  cursor?: number;
  kind: PublicationKind;
  changes: DatabaseSemanticChange[];
  changeIndex?: Map<string, DatabaseSemanticChange>;
  at: number;
  boundary: number;
};

export class DatabaseMutationHistory implements StoreHistoryOverride {
  private readonly undoStack: Entry[] = [];
  private readonly redoStack: Entry[] = [];
  private readonly revision = signal(0);
  private boundary = 0;
  private busy = false;
  private readonly cleanupErrors: unknown[] = [];
  private readonly cleanups = new Set<Promise<void>>();
  constructor(
    commit: Commit,
    private readonly exclusive: (
      run: (commit: Commit) => Promise<LocalPublicationReceipt | null>
    ) => Promise<LocalPublicationReceipt | null> = run => run(commit)
  ) {}
  get canUndo() {
    this.revision.value;
    return this.undoStack.length > 0;
  }
  get canRedo() {
    this.revision.value;
    return this.redoStack.length > 0;
  }
  captureSync() {
    this.boundary++;
  }
  /** `join` appends to the previous entry regardless of the capture window:
   * a follow-up layout change belongs to the write that caused it. */
  record(
    kind: PublicationKind,
    changes: DatabaseSemanticChange[],
    join = false
  ): void {
    if (!changes.length) return;
    const at = Date.now(),
      last = this.undoStack.at(-1);
    if (
      last &&
      !last.deferred &&
      (join ||
        (last.boundary === this.boundary &&
          at >= last.at &&
          at - last.at < 500))
    ) {
      const merged =
        last.changeIndex ??
        new Map(
          last.changes
            .filter(change => !equal(change.before, change.after))
            .map(change => [
              JSON.stringify([change.docId, change.collection, change.path]),
              change,
            ])
        );
      for (const change of changes) {
        const key = JSON.stringify([
            change.docId,
            change.collection,
            change.path,
          ]),
          previous = merged.get(key);
        const next = previous ? { ...change, before: previous.before } : change;
        if (!equal(next.before, next.after)) merged.set(key, next);
        else {
          merged.delete(key);
          // A value created and removed within this entry takes its nested
          // edits with it; replaying them would address a missing parent.
          if (next.before.kind === 'value' && next.before.value === undefined) {
            const parent = JSON.stringify(change.path);
            for (const [nestedKey, nested] of merged)
              if (
                nested.docId === change.docId &&
                nested.collection === change.collection &&
                nested.path.length > change.path.length &&
                JSON.stringify(nested.path.slice(0, change.path.length)) ===
                  parent
              )
                merged.delete(nestedKey);
          }
        }
      }
      // Retain one index through the capture window. Materialize only on undo/
      // redo, instead of rebuilding and comparing the entire history per ACK.
      last.changeIndex = merged;
      last.changes = [];
      last.at = at;
    } else this.push({ kind, changes, at, boundary: this.boundary });
    this.discardRedo();
    this.revision.value++;
  }
  private push(entry: Entry) {
    this.undoStack.push(entry);
    for (const dropped of this.undoStack.splice(
      0,
      Math.max(0, this.undoStack.length - DATABASE_UNDO_STEP_LIMIT)
    ))
      this.release(dropped);
  }
  private release(entry: Entry) {
    if (!entry.deferred) return;
    const cleanup = entry.deferred.release().catch(error => {
      this.cleanupErrors.push(error);
    });
    this.cleanups.add(cleanup);
    void cleanup.then(() => this.cleanups.delete(cleanup));
  }
  private discardRedo() {
    for (const entry of this.redoStack) this.release(entry);
    this.redoStack.length = 0;
  }
  recordDeferred(kind: PublicationKind, deferred: DeferredDatabaseHistory) {
    if (!deferred.count) throw new PublicationError('FORMAT_MISMATCH');
    this.captureSync();
    this.push({
      kind,
      changes: [],
      at: Date.now(),
      boundary: this.boundary,
      deferred,
      cursor: deferred.count,
    });
    this.discardRedo();
    this.captureSync();
    this.revision.value++;
  }
  /** Enter the writer queue synchronously: a flush issued right after
   * undo()/redo() must already observe the replay as a pending publication. */
  private replay(
    undo: boolean
  ): Promise<LocalPublicationReceipt | null | undefined> {
    if (this.busy)
      return Promise.reject(new PublicationError('LOCAL_CONFLICT'));
    const from = undo ? this.undoStack : this.redoStack;
    const to = undo ? this.redoStack : this.undoStack;
    // Stacks change only inside replay/record; cleanups release payloads only.
    const entry = from.at(-1);
    if (!entry) return Promise.resolve(undefined);
    this.busy = true;
    // Writes recorded while the replay waits on the writer tail start a new
    // entry instead of merging into the one being replayed.
    this.captureSync();
    return this.exclusive(async commit => {
      await Promise.all(this.cleanups);
      if (this.cleanupErrors.length)
        throw new AggregateError(
          this.cleanupErrors.splice(0),
          'IMPORT_HISTORY_CLEANUP_FAILED'
        );
      let last: LocalPublicationReceipt | null | undefined;
      const publish = async (changes: DatabaseSemanticChange[]) => {
        last = await commit({
          kind: undo ? inverseKind(entry.kind) : entry.kind,
          changes: new Map(),
          restore: undo
            ? [...changes].reverse().map(change => ({
                ...change,
                before: change.after,
                after: change.before,
              }))
            : changes,
          interactive: false,
        });
      };
      if (entry.deferred) {
        // Advance only on durable ACK. A failed page is retried without
        // replaying already acknowledged pages or loading the whole import.
        while (
          undo ? entry.cursor! > 0 : entry.cursor! < entry.deferred.count
        ) {
          const index = undo ? entry.cursor! - 1 : entry.cursor!;
          await publish(await entry.deferred.read(index));
          entry.cursor = undo ? index : index + 1;
        }
      } else await publish([...(entry.changeIndex?.values() ?? entry.changes)]);
      if (!last) throw new PublicationError('FORMAT_MISMATCH');
      // A newer write may have discarded the redo stack meanwhile.
      const index = from.lastIndexOf(entry);
      if (index >= 0) from.splice(index, 1);
      to.push(entry);
      this.boundary++;
      this.revision.value++;
      return last;
    }).finally(() => {
      this.busy = false;
    });
  }
  undo() {
    return this.replay(true);
  }
  redo() {
    return this.replay(false);
  }
}
